import { KeyboardEvent, ReactElement } from 'react'
import { v4 } from 'uuid'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import StopIcon from '@mui/icons-material/Stop'
import { Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import type { CopilotChatInputProps } from '@copilotkit/react-core/v2'
import { useCopilotChatConfiguration, useCopilotKit } from '@copilotkit/react-core/v2/headless'
import { CHAT_LABELS } from '../utils'
import MarkedElementsChip from './MarkedElementsChip'

/**
 * The chat's message field, in our own components, replacing CopilotKit's
 * input slot. CopilotChat keeps the value and clears it after sending; Enter
 * sends, Shift+Enter starts a new line, and while the agent works the button
 * stops the turn. Sending is not offered until the chat is connected
 * (`onSubmitMessage` is missing until then). Attachments and dictation are
 * not enabled in this chat, so their buttons are left out. Approval mode and
 * disclaimer sit under the chat (`ChatBody`).
 *
 * A message sent while approval cards are open sets them aside: the AG-UI
 * client refuses a run that leaves an open interrupt unanswered, so the
 * message goes out with the open cards as `cancelled`. The agent service
 * treats that as a new message and answers the open changes as "Nicht
 * ausgeführt"; the API records them as denied.
 */
const ChatInput = ({
  value = '',
  onChange,
  onSubmitMessage,
  onStop,
  isRunning = false,
}: CopilotChatInputProps): ReactElement => {
  const { copilotkit } = useCopilotKit()
  const chatConfiguration = useCopilotChatConfiguration()
  const canSend = !isRunning && !!onSubmitMessage && value.trim() !== ''

  const send = async () => {
    if (!canSend) return
    const message = value.trim()
    const agent = chatConfiguration ? copilotkit.getAgent(chatConfiguration.agentId) : undefined
    const openInterrupts = agent?.pendingInterrupts ?? []
    if (!agent || openInterrupts.length === 0) {
      onSubmitMessage(message)
      return
    }
    onChange?.('')
    agent.addMessage({ id: v4(), role: 'user', content: message })
    try {
      await copilotkit.runAgent({
        agent,
        resume: openInterrupts.map(interrupt => ({
          interruptId: interrupt.id,
          status: 'cancelled' as const,
        })),
      })
    } catch {
      // The CopilotKit provider reports failed runs (`onError`), as for any
      // message sent by the chat itself.
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    send()
  }

  return (
    // CopilotChat lays the input over the bottom of the messages in a layer
    // that lets clicks through; the input itself takes them.
    <Box
      sx={{
        pointerEvents: 'auto',
        bgcolor: 'background.paper',
        px: 1,
        pt: 1,
        pb: 0.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MarkedElementsChip />
      <TextField
        value={value}
        onChange={event => onChange?.(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={CHAT_LABELS.chatInputPlaceholder}
        multiline
        maxRows={6}
        fullWidth
        size="small"
        autoFocus
        inputProps={{ 'data-cy': 'ai-chat-input' }}
        sx={{ '& .MuiInputBase-root': { fontSize: 14 } }}
        InputProps={{
          endAdornment: (
            <InputAdornment
              position="end"
              // Exactly one text line high (the field's line height) at the
              // bottom of the text, with the button centred on it: centred in a
              // single-line field whatever the button's size, and next to the
              // last line as the field grows.
              sx={{ alignSelf: 'flex-end', height: '1.4375em', maxHeight: 'none' }}
            >
              {isRunning ? (
                <Tooltip title="Anhalten">
                  <IconButton size="small" onClick={onStop} data-cy="ai-chat-stop">
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Senden [Enter]">
                  {/* A disabled button fires no events, so the tooltip needs a
                      wrapper; a flex one, as an inline span's line box would
                      make it taller than the button and move the button off
                      centre. */}
                  <Box component="span" sx={{ display: 'flex' }}>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={!canSend}
                      onClick={send}
                      data-cy="ai-chat-send"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  )
}

export default ChatInput
